import {
  allNewFieldsDraftLinkedAsset,
  allUpdatedFieldsDraftLinkedAsset,
  draftLinkedAsset,
  draftLinkedAssetWithEmptyFields,
  draftLinkedAssetWithEmptyFieldsComparedToLive,
  draftLinkedAssetWithNullFieldsComparedToLive,
} from 'mocks/server/data/draftLinkedAssets';
import {
  baseEntity,
  baseEntityWithEmptyFields,
  liveBaseEntity,
} from 'mocks/server/data/generation/generateBaseEntity';
import {
  formatSize,
  generateFrameName,
  linkedAssetFrame,
} from 'mocks/server/data/generation/generateDetails';
import { baseLinkedAsset } from 'mocks/server/data/linkedAssets';
import {
  BroadsignFrame,
  IDraftEntityResponse,
  IDraftLinkedAsset,
  LinkedAssetFrame,
} from 'types/detailsEntityType';
import { EntityType } from 'types/entityType';
import { LiveStatus } from 'utils/statuses';

export const defaultDraftLinkedAssetResponse = (linkedAsset: IDraftLinkedAsset) => ({
  draft: linkedAsset,
  previous: {
    ...liveBaseEntity(linkedAsset),
    linkedAssetFrames: linkedAsset.linkedAssetFrames ? linkedAsset.linkedAssetFrames : [],
  },
});

export const draftLinkedAssetHandler = {
  [draftLinkedAsset.spaceId]: (linkedAsset: IDraftLinkedAsset): IDraftEntityResponse => {
    return defaultDraftLinkedAssetResponse(linkedAsset);
  },

  [draftLinkedAssetWithEmptyFields.spaceId]: (
    linkedAsset: IDraftLinkedAsset
  ): IDraftEntityResponse => ({
    draft: linkedAsset,
    previous: { ...liveBaseEntity(linkedAsset), linkedAssetFrames: [] },
  }),

  [allNewFieldsDraftLinkedAsset.spaceId]: (
    linkedAsset: IDraftLinkedAsset
  ): IDraftEntityResponse => ({
    draft: linkedAsset,
    previous: {
      ...baseEntityWithEmptyFields(linkedAsset.spaceId, EntityType.LINKED_ASSET),
      linkedAssetFrames: [],
      status: LiveStatus.INACTIVE,
      hasDraft: false,
    },
  }),

  [allUpdatedFieldsDraftLinkedAsset.spaceId]: (
    linkedAsset: IDraftLinkedAsset
  ): IDraftEntityResponse => {
    const liveLinkedAsset = baseLinkedAsset();
    const currentLinkedFrames = linkedAsset.linkedAssetFrames || [];
    const modifiedLinkedFrame: LinkedAssetFrame = {
      ...currentLinkedFrames[0],
      name: 'modified',
    };

    const broadsignFrames = currentLinkedFrames[1].broadsignFrames ?? [];

    const removedLinkedAssetBroadsignFrame: BroadsignFrame = {
      ...broadsignFrames[0],
      broadsignFrameId: 'removed',
      displayUnitId: 'removed',
    };

    const modifiedBroadsignFrames: LinkedAssetFrame = {
      ...currentLinkedFrames[1],
      name: 'modifiedBroadsignFrames',
      broadsignFrames: [broadsignFrames[1], broadsignFrames[2], removedLinkedAssetBroadsignFrame],
    };
    return {
      draft: linkedAsset,
      previous: {
        ...liveLinkedAsset,
        spaceId: allUpdatedFieldsDraftLinkedAsset.spaceId,
        mediaOwnerName: 'oldMediaOwnerName',
        environment: 'oldEnvironment',
        formatSize: formatSize(),
        internetAccessAvailable: !linkedAsset.internetAccessAvailable,
        linkedAssetFrames: [
          currentLinkedFrames[3],
          modifiedLinkedFrame,
          modifiedBroadsignFrames,
          linkedAssetFrame(generateFrameName()),
        ],
      },
    };
  },

  [draftLinkedAssetWithNullFieldsComparedToLive.spaceId]: (
    linkedAsset: IDraftLinkedAsset
  ): IDraftEntityResponse => ({
    draft: linkedAsset,
    previous: {
      ...baseEntity(EntityType.LINKED_ASSET),
      spaceId: draftLinkedAssetWithNullFieldsComparedToLive.spaceId,
      linkedAssetFrames: [linkedAssetFrame(generateFrameName())],
      status: LiveStatus.ACTIVE,
      hasDraft: false,
    },
  }),

  [draftLinkedAssetWithEmptyFieldsComparedToLive.spaceId]: (
    linkedAsset: IDraftLinkedAsset
  ): IDraftEntityResponse => ({
    draft: linkedAsset,
    previous: {
      ...baseEntity(EntityType.LINKED_ASSET),
      spaceId: draftLinkedAssetWithEmptyFieldsComparedToLive.spaceId,
      linkedAssetFrames: [linkedAssetFrame(generateFrameName())],
      status: LiveStatus.ACTIVE,
      hasDraft: false,
    },
  }),
};
